/**
 * Utility for currency conversion to EUR.
 * Used for evaluating EU compliance threshold (€150).
 *
 * Integrates with GET /shipment/exchangeRate?from=<currency>&to=EUR
 */

import { useState, useEffect } from 'react';
import API from '../services/apiAxios';

export interface ExchangeRateApiResponse {
  msg?: string;
  data?: {
    from: string;
    to: string;
    currencyRate: number;
  };
}

// In-memory session cache: Map<currencyCode, rateAgainstEur>
const eurRateSessionCache = new Map<string, number>();

/**
 * Returns the cached conversion rate from the given currency to EUR, or null if not yet cached.
 * Returns 1.0 immediately if currency is EUR.
 */
export function getEurConversionRate(currency: string): number | null {
  if (!currency) return 1.0;
  const upper = currency.trim().toUpperCase();
  if (upper === 'EUR') return 1.0;
  return eurRateSessionCache.get(upper) ?? null;
}

/**
 * Fetches the live conversion rate from the backend API:
 * GET /shipment/exchangeRate?from=<currency>&to=EUR
 * Caches the result in eurRateSessionCache for the current session.
 */
export async function fetchEurConversionRate(fromCurrency: string): Promise<number> {
  if (!fromCurrency) return 1.0;
  const upper = fromCurrency.trim().toUpperCase();
  if (upper === 'EUR') return 1.0;

  if (eurRateSessionCache.has(upper)) {
    return eurRateSessionCache.get(upper)!;
  }

  try {
    const res = await API.get<ExchangeRateApiResponse>('/shipment/exchangeRate', {
      params: { from: upper, to: 'EUR' },
    });
    const rate = res?.data?.data?.currencyRate;
    if (typeof rate !== 'number' || isNaN(rate) || rate <= 0) {
      throw new Error(`Invalid exchange rate received for ${upper}`);
    }
    eurRateSessionCache.set(upper, rate);
    return rate;
  } catch (err) {
    // TODO: remove after verification
    console.warn(`[ExchangeRate] Failed to fetch EUR exchange rate for ${upper}:`, err);
    throw err;
  }
}

/**
 * Converts a numeric amount in a specified currency to EUR using the provided rate
 * or the cached rate. Returns 0 if amount is invalid or rate is unavailable.
 */
export function convertAmountToEur(amount: number, currency: string, customRate?: number | null): number {
  if (isNaN(amount) || amount <= 0) return 0;
  const upper = (currency || '').trim().toUpperCase();
  if (upper === 'EUR') return Number(amount.toFixed(2));

  const rate =
    customRate !== undefined && customRate !== null && customRate > 0 ? customRate : getEurConversionRate(currency);

  if (!rate || rate <= 0) return 0;
  return Number((amount * rate).toFixed(2));
}

/**
 * Custom React hook to fetch and cache the EUR exchange rate for a given currency.
 * Only calls the backend API when isEU is true and currency is not EUR.
 */
export function useEurExchangeRate({ currency, isEU }: { currency?: string; isEU?: boolean }): {
  rate: number | null;
  loading: boolean;
  error: any;
} {
  const upper = (currency || '').trim().toUpperCase();
  const shouldFetch = Boolean(isEU && upper && upper !== 'EUR');

  const [state, setState] = useState<{
    rate: number | null;
    loading: boolean;
    error: any;
  }>(() => {
    if (!isEU) return { rate: null, loading: false, error: null };
    if (upper === 'EUR') return { rate: 1.0, loading: false, error: null };
    const cached = eurRateSessionCache.get(upper);
    if (cached !== undefined) return { rate: cached, loading: false, error: null };
    return { rate: null, loading: shouldFetch, error: null };
  });

  useEffect(() => {
    if (!isEU) {
      setState({ rate: null, loading: false, error: null });
      return;
    }

    if (upper === 'EUR') {
      setState({ rate: 1.0, loading: false, error: null });
      return;
    }

    if (!upper) {
      setState({ rate: null, loading: false, error: null });
      return;
    }

    const cached = eurRateSessionCache.get(upper);
    if (cached !== undefined) {
      setState({ rate: cached, loading: false, error: null });
      return;
    }

    let isMounted = true;
    setState(prev => ({ ...prev, loading: true, error: null }));

    fetchEurConversionRate(upper)
      .then(rate => {
        if (isMounted) {
          setState({ rate, loading: false, error: null });
        }
      })
      .catch(error => {
        if (isMounted) {
          setState({ rate: null, loading: false, error });
        }
      });

    return () => {
      isMounted = false;
    };
  }, [upper, isEU]);

  return state;
}
